import axiosIns from '@axios'
import type { AxiosRequestConfig } from 'axios'

const $http = axiosIns
export const AWS_FILE_OPERATION_TIMEOUT_MS = 1200000
const FILE_UPLOAD_TIMEOUT_MS = AWS_FILE_OPERATION_TIMEOUT_MS
export const WAITING_ROOM_ARCHIVE_APPROVE_TIMEOUT_MS = AWS_FILE_OPERATION_TIMEOUT_MS
const WAITING_ROOM_APPROVE_REQUEST_CONFIG: AxiosRequestConfig = {
  timeout: AWS_FILE_OPERATION_TIMEOUT_MS,
}
const FILE_UPLOAD_REQUEST_CONFIG = {
  headers: { 'Content-Type': 'multipart/form-data' },
  timeout: FILE_UPLOAD_TIMEOUT_MS,
}

export type FileManagementFolder = 'ARTWORK' | 'PREPRESS_PROOF' | 'INSTALLATION' | 'SCOPE' | 'SCOPING_PHOTOS'
export type FileManagementItemType = 0 | 1 | 2 | 3 | 4
export type WaitingRoomIssueType = 1 | 2 | 3 | 4 | 5
export type WaitingRoomIssueTypeQueryValue = 'filename-mismatch' | 'status-mismatch' | 'duplicate-files' | 'all'
export type WaitingRoomProjectType = 0 | 1 | 2 | 3 | 4 | 5
export type WaitingRoomProjectTypeQueryValue = 'wayfinding' | 'branding' | 'all'

export const PROJECT_TYPE_RETAIL: WaitingRoomProjectType = 0
export const PROJECT_TYPE_WAYFINDING: WaitingRoomProjectType = 1
export const PROJECT_TYPE_BRANDING: WaitingRoomProjectType = 2
export const PROJECT_TYPE_FA_SIGNAGE: WaitingRoomProjectType = 3
export const PROJECT_TYPE_QRAIL: WaitingRoomProjectType = 4
export const PROJECT_TYPE_KOP: WaitingRoomProjectType = 5

export const WAITING_ROOM_PROJECT_TYPE_OPTIONS: Array<{
  title: string
  value: WaitingRoomProjectType | null
  queryValue: WaitingRoomProjectTypeQueryValue
}> = [
  { title: 'Wayfinding', value: PROJECT_TYPE_WAYFINDING, queryValue: 'wayfinding' },
  { title: 'Branding', value: PROJECT_TYPE_BRANDING, queryValue: 'branding' },
  { title: 'All', value: null, queryValue: 'all' },
]

const WAITING_ROOM_PROJECT_TYPE_TITLES: Record<WaitingRoomProjectType, string> = {
  [PROJECT_TYPE_RETAIL]: 'Retail',
  [PROJECT_TYPE_WAYFINDING]: 'Wayfinding',
  [PROJECT_TYPE_BRANDING]: 'Branding',
  [PROJECT_TYPE_FA_SIGNAGE]: 'FA Signage',
  [PROJECT_TYPE_QRAIL]: 'QRail',
  [PROJECT_TYPE_KOP]: 'KOP',
}

export const getWaitingRoomProjectTypeTitle = (projectType?: number | null): string => {
  return WAITING_ROOM_PROJECT_TYPE_TITLES[Number(projectType) as WaitingRoomProjectType] ?? 'Unknown'
}

export const getWaitingRoomProjectTypeQueryValue = (
  projectType?: number | null,
): WaitingRoomProjectTypeQueryValue => {
  if (projectType === PROJECT_TYPE_WAYFINDING) return 'wayfinding'
  if (projectType === PROJECT_TYPE_BRANDING) return 'branding'
  return 'all'
}

export const WAITING_ROOM_ISSUE_TYPE_OPTIONS: Array<{
  title: string
  value: WaitingRoomIssueType | null
  queryValue: WaitingRoomIssueTypeQueryValue
}> = [
  { title: 'Filename Mismatch', value: 1, queryValue: 'filename-mismatch' },
  { title: 'Status Mismatch', value: 2, queryValue: 'status-mismatch' },
  { title: 'Duplicate Files', value: 3, queryValue: 'duplicate-files' },
  { title: 'All', value: null, queryValue: 'all' },
]

export const FILE_MANAGEMENT_ITEM_TYPE_OPTIONS: Array<{
  title: string
  value: FileManagementItemType
  folder: FileManagementFolder
}> = [
  { title: 'Artwork', value: 0, folder: 'ARTWORK' },
  { title: 'Prepress Proof', value: 1, folder: 'PREPRESS_PROOF' },
  { title: 'Installation', value: 2, folder: 'INSTALLATION' },
  { title: 'Scope', value: 3, folder: 'SCOPE' },
  { title: 'Scoping Photos', value: 4, folder: 'SCOPING_PHOTOS' },
]

export const FILE_MANAGEMENT_FOLDERS = [
  { title: 'Artwork', value: 'ARTWORK', itemType: 0 },
  { title: 'Installation Photos', value: 'INSTALLATION', itemType: 2 },
  { title: 'Prepress Proof', value: 'PREPRESS_PROOF', itemType: 1 },
  { title: 'Scope', value: 'SCOPE', itemType: 3 },
  { title: 'Scoping Photos', value: 'SCOPING_PHOTOS', itemType: 4 },
]

export const folderToItemType = (folder?: string | null): FileManagementItemType | null => {
  return FILE_MANAGEMENT_ITEM_TYPE_OPTIONS.find(option => option.folder === folder)?.value ?? null
}

export const itemTypeToFolder = (itemType?: number | null): FileManagementFolder | null => {
  return FILE_MANAGEMENT_ITEM_TYPE_OPTIONS.find(option => option.value === Number(itemType))?.folder ?? null
}

export const getItemTypeTitle = (itemType?: number | null): string => {
  return FILE_MANAGEMENT_ITEM_TYPE_OPTIONS.find(option => option.value === Number(itemType))?.title ?? 'Unknown'
}

export const FILE_MANAGEMENT_FOLDER_COLUMNS = {
  1: {
    left: ['ARTWORK', 'PREPRESS_PROOF'],
    right: ['INSTALLATION', 'SCOPING_PHOTOS'],
  },
  2: {
    left: ['ARTWORK', 'PREPRESS_PROOF', 'SCOPE'],
    right: ['INSTALLATION', 'SCOPING_PHOTOS'],
  },
  5: {
    left: ['ARTWORK', 'PREPRESS_PROOF', 'SCOPE'],
    right: ['INSTALLATION', 'SCOPING_PHOTOS'],
  },
} as const

export type FileManagementFolderValue = typeof FILE_MANAGEMENT_FOLDERS[number]['value']
export type FileManagementFolderColumn = 'left' | 'right'

const DEFAULT_FOLDER_ORDER = ['ARTWORK', 'INSTALLATION', 'PREPRESS_PROOF', 'SCOPE', 'SCOPING_PHOTOS'] as const

export const getFileManagementFolders = (projectTypeId?: number | null) => {
  const columns = projectTypeId ? FILE_MANAGEMENT_FOLDER_COLUMNS[projectTypeId as keyof typeof FILE_MANAGEMENT_FOLDER_COLUMNS] : null
  const folderValues = columns
    ? [...columns.left, ...columns.right]
    : [...DEFAULT_FOLDER_ORDER]
  const allowedValues = new Set<string>(folderValues)

  return FILE_MANAGEMENT_FOLDERS.filter(folder => allowedValues.has(folder.value))
}

export const getProjectFilesFolderColumns = (projectTypeId?: number | null) => {
  const columns = projectTypeId ? FILE_MANAGEMENT_FOLDER_COLUMNS[projectTypeId as keyof typeof FILE_MANAGEMENT_FOLDER_COLUMNS] : null
  if (!columns) {
    return {
      left: ['ARTWORK', 'PREPRESS_PROOF'] as string[],
      right: ['INSTALLATION', 'SCOPE', 'SCOPING_PHOTOS'] as string[],
    }
  }

  return {
    left: [...columns.left],
    right: [...columns.right],
  }
}

export interface ProjectFileUploadResultItem {
  filename: string
  key?: string
  item_file_id?: string
  folder?: string
  code?: string
  campaign_id?: string
  campaign_name?: string | null
  error?: string
}

export interface UploadStatusWarning {
  filename: string
  code: string
  item_type: 'sign' | 'line_item'
  item_id: number
  item_display_name: string
  current_status: string | null
  expected_status: string
  reason: 'wrong_status' | 'status_update_failed'
  message: string
  link: string
}

export interface ProjectFilesUploadResponse {
  uploaded_files: ProjectFileUploadResultItem[]
  replaced_files: ProjectFileUploadResultItem[]
  not_uploaded_files: ProjectFileUploadResultItem[]
  filename_issue_files?: ProjectFileUploadResultItem[]
  waiting_room_files?: WaitingRoomFile[]
  status_warnings: UploadStatusWarning[]
  summary: {
    uploaded: number
    replaced: number
    not_uploaded: number
    waiting_room?: number
    filename_issues?: number
    status_warnings: number
  }
}

export interface WaitingRoomFile {
  id: string
  original_filename: string
  filename: string
  name: string
  new_filename: string
  extension: string
  folder: string
  item_type: FileManagementItemType
  item_type_name: string
  campaign_type: number
  approval_scope?: Record<string, unknown>
  campaign: number | null
  campaign_id: number | null
  campaign_name: string | null
  event_group?: number | null
  event_group_name?: string | null
  issue_type?: WaitingRoomIssueType | null
  issue_type_name?: string | null
  brand_book_codes: string[]
  brand_book_code_states?: Array<{
    code: string
    passed: boolean
  }>
  is_virtual?: boolean
  is_partially_matched?: boolean
  source_file_id?: string | null
  source_file_campaign_id?: number | string | null
  current_statuses: string[]
  expected_statuses: string[]
  chat_messages_count: number
  upload_error?: string | null
  is_rejected: boolean
  is_accepted: boolean
  accepted_file?: string | null
  accepted_target_type?: string | null
  accepted_target_id?: string | null
  created_by?: number | null
  created_by_display?: string | null
  created?: string
  modified?: string
}

export interface WaitingRoomFileListResponse {
  results: WaitingRoomFile[]
  count: number
  next: boolean
}

export interface WaitingRoomItemOption {
  identity: string
}

export interface WaitingRoomItemOptionsResponse {
  results: WaitingRoomItemOption[]
  count: number
  next: boolean
}

export interface WaitingRoomStatusMismatch {
  target_type: 'sign' | 'line_item' | 'kop_item'
  item_label: string
  identity: string
  item_id: number
  current_status: string
  expected_system_status: string
}

export interface WaitingRoomOptionsResponse {
  campaign_options: Array<{
    id: number
    name: string
    type: number
    event_group?: number | null
    event_group_name?: string | null
  }>
  event_group_options: Array<{
    id: number
    name: string
  }>
}

export interface WaitingRoomProjectOption {
  id: number
  name: string
  type: number
  event_group?: number | null
  event_group_name?: string | null
}

export interface WaitingRoomProjectOptionsResponse {
  results: WaitingRoomProjectOption[]
  count: number
  next: boolean
}

export interface WaitingRoomDuplicateComparisonFile {
  id: string
  filename: string
  name?: string
  extension?: string
  key?: string
  url?: string | null
  preview_url?: string | null
  download_url?: string | null
  size?: number
  content_type?: string
  etag?: string
  last_modified?: string
  file?: WaitingRoomFile
}

export interface WaitingRoomDuplicateComparisonResponse {
  comparison_mode?: 'file_management' | 'waiting_room_only'
  current_main_file: WaitingRoomDuplicateComparisonFile | null
  current_waiting_room_file?: WaitingRoomDuplicateComparisonFile | null
  waiting_room_file: WaitingRoomDuplicateComparisonFile
  waiting_room_files?: WaitingRoomDuplicateComparisonFile[]
  waiting_room_files_count?: number
  target: {
    target_type: 'sign' | 'line_item' | 'kop_item'
    target_id: string
  }
  identity: string
  folder: FileManagementFolder
}

export interface WaitingRoomDuplicateComparisonWaitingRoomFilesResponse {
  waiting_room_files: WaitingRoomDuplicateComparisonFile[]
  waiting_room_files_count: number
}

export const $uploadProjectFiles = async (projectId: number | string, payload: FormData) => {
  return await $http.post<ProjectFilesUploadResponse>(
    `file-management/projects/${projectId}/files/`,
    payload,
    FILE_UPLOAD_REQUEST_CONFIG,
  )
}

export const $uploadGlobalProjectFiles = async (payload: FormData) => {
  return await $http.post<ProjectFilesUploadResponse>(
    'file-management/files/global-upload/',
    payload,
    FILE_UPLOAD_REQUEST_CONFIG,
  )
}

export const $uploadSelectedItemProjectFiles = async (payload: FormData) => {
  return await $http.post<ProjectFilesUploadResponse>(
    'file-management/files/selected-items-upload/',
    payload,
    FILE_UPLOAD_REQUEST_CONFIG,
  )
}

export const $createWaitingRoomFiles = async (payload: FormData) => {
  return await $http.post<{ results: WaitingRoomFile[] }>(
    'file-management/waiting-room/files/',
    payload,
    FILE_UPLOAD_REQUEST_CONFIG,
  )
}

export const $getWaitingRoomFiles = async (params: Record<string, any>, signal?: AbortSignal) => {
  return await $http.get<WaitingRoomFileListResponse>('file-management/waiting-room/files/', { params, signal })
}

export const $getWaitingRoomOptions = async (params: Record<string, any>, signal?: AbortSignal) => {
  return await $http.get<WaitingRoomOptionsResponse>('file-management/waiting-room/options/', { params, signal })
}

export const $getWaitingRoomProjectOptions = async (params: Record<string, any>) => {
  return await $http.get<WaitingRoomProjectOptionsResponse>('file-management/waiting-room/project-options/', { params })
}

export const $getWaitingRoomItemOptions = async (params: Record<string, any>) => {
  return await $http.get<WaitingRoomItemOptionsResponse>('file-management/waiting-room/items/', { params })
}

export const $getUploadItemOptions = async (params: Record<string, any>) => {
  return await $http.get<WaitingRoomItemOptionsResponse>('file-management/files/upload-item-options/', { params })
}

export const $updateWaitingRoomFile = async (id: string, payload: object) => {
  return await $http.patch<WaitingRoomFile>(`file-management/waiting-room/files/${id}/`, payload)
}

export const $acceptWaitingRoomFile = async (
  id: string,
  payload?: {
    duplicate_resolution?: 'waiting_room_file' | 'current_file'
    reject_waiting_room_file_ids?: string[]
    bypass_duplicate_status_validation?: boolean
    skip_status_validation?: boolean
    archive_replacement_file_id?: string
  },
  config: AxiosRequestConfig = WAITING_ROOM_APPROVE_REQUEST_CONFIG,
) => {
  return await $http.post<{
    file: WaitingRoomFile
    uploaded_files: ProjectFileUploadResultItem[]
    status_warnings: UploadStatusWarning[]
  }>(`file-management/waiting-room/files/${id}/accept/`, payload || {}, config)
}

export const $rejectWaitingRoomFile = async (id: string) => {
  return await $http.post<WaitingRoomFile>(`file-management/waiting-room/files/${id}/reject/`)
}

export const $restoreWaitingRoomFile = async (id: string) => {
  return await $http.post<WaitingRoomFile>(`file-management/waiting-room/files/${id}/restore/`)
}

export const $resolveWaitingRoomOnlyDuplicate = async (
  id: string,
  payload: { candidate_file_id: string; keep: 'status_mismatch' | 'waiting_room_duplicate' },
) => {
  return await $http.post<{ kept_file_id: string }>(
    `file-management/waiting-room/files/${id}/resolve-waiting-room-duplicate/`,
    payload,
  )
}

export const $getWaitingRoomFilePreviewUrl = async (id: string, expiresIn = 900) => {
  return await $http.get(`file-management/waiting-room/files/${id}/preview/`, {
    params: { expires_in: expiresIn, intent: 'preview' },
  })
}

export const $getWaitingRoomFileDownloadUrl = async (id: string, expiresIn = 900) => {
  return await $http.get(`file-management/waiting-room/files/${id}/preview/`, {
    params: { expires_in: expiresIn, intent: 'download' },
  })
}

export const $getWaitingRoomDuplicateComparison = async (
  id: string,
  expiresIn = 900,
  includeMatchingWaitingRoomFiles = false,
  duplicateLikeOnly = false,
  archiveReplacementFileId?: string,
) => {
  return await $http.get<WaitingRoomDuplicateComparisonResponse>(
    `file-management/waiting-room/files/${id}/duplicate-comparison/`,
    {
      params: {
        expires_in: expiresIn,
        include_matching_waiting_room_files: includeMatchingWaitingRoomFiles,
        duplicate_like_only: duplicateLikeOnly,
        archive_replacement_file_id: archiveReplacementFileId,
      },
    },
  )
}

export const $getWaitingRoomDuplicateComparisonWaitingRoomFiles = async (
  id: string,
  expiresIn = 900,
  duplicateLikeOnly = false,
  archiveReplacementFileId?: string,
) => {
  return await $http.get<WaitingRoomDuplicateComparisonWaitingRoomFilesResponse>(
    `file-management/waiting-room/files/${id}/duplicate-comparison/waiting-room-files/`,
    {
      params: {
        expires_in: expiresIn,
        duplicate_like_only: duplicateLikeOnly,
        archive_replacement_file_id: archiveReplacementFileId,
      },
    },
  )
}

export interface ProjectFileLinkedItems {
  sign_ids: string[]
  line_item_ids: string[]
  kop_item_ids: string[]
}

export interface ProjectFileRecord {
  id: string
  key?: string
  name: string
  extension: string
  filename?: string
  path: string
  preview_path?: string | null
  preview_url?: string | null
  folder: string
  campaign_id: string
  campaign_name?: string | null
  campaign_type: number
  work_order_id?: number | string | null
  work_order_no?: string | null
  item_type: number
  identity: string
  is_removed: boolean
  is_historical: boolean
  list_state?: 'main' | 'historical'
  created?: string
  modified?: string
  created_by?: number | null
  created_by_display?: string | null
  modified_by?: number | null
  modified_by_display?: string | null
  created_at?: string
  updated_at?: string
  linked_items: ProjectFileLinkedItems
}

export interface ProjectFileGroupRecord extends ProjectFileRecord {
  version_count: number
  created_at: string
  updated_at: string
  statuses: string[]
}

export interface ProjectFilesListResponse {
  results: ProjectFileRecord[]
}

export interface ProjectFilesBatchScope {
  project_id: number | string
  selected_item_ids?: number[]
  work_order_id?: number | string | null
}

export interface ProjectFilesBatchScopeResponse {
  project_id: number | string
  campaign_id: string
  campaign_name?: string | null
  work_order_id?: number | string | null
  work_order_no?: string | null
  results: ProjectFileRecord[]
}

export interface ProjectFilesBatchListResponse {
  scopes: ProjectFilesBatchScopeResponse[]
  results: ProjectFileRecord[]
}

export interface ProjectFileGroupsScopeResponse extends Omit<ProjectFilesBatchScopeResponse, 'results'> {
  results: ProjectFileGroupRecord[]
  folder_results?: ProjectFileFolderResults
}

export interface ProjectFileFolderPage {
  results: ProjectFileGroupRecord[]
  count: number
  page: number
  per_page: number
  total_pages: number
}

export type ProjectFileFolderResults = Record<string, ProjectFileFolderPage>

export type ProjectFileFolderPagesRequest = Record<string, {
  page: number
  per_page: number
}>

export interface ProjectFileGroupsListResponse {
  scopes: ProjectFileGroupsScopeResponse[]
  results: ProjectFileGroupRecord[]
  folder_results?: ProjectFileFolderResults
  program_options?: Array<{
    id: number
    name: string
  }>
}

export interface ProjectFileGroupPageRequest {
  project_id: number | string
  folder: FileManagementFolder | null
  page: number
  per_page: number
}

export interface ProjectFileGroupMetadata {
  project_id: number | string
  campaign_id: string
  campaign_name?: string | null
  count: number
  folder_counts: Partial<Record<FileManagementFolder, number>>
}

export interface ProjectFileGroupPagesRequest {
  scopes: ProjectFilesBatchScope[]
  pages?: ProjectFileGroupPageRequest[]
  search?: string
  program_ids?: number[]
  campaign_type?: number | null
  ordering?: string
  include_metadata?: boolean
  selected_files?: Array<{ project_id: number | string; key: string }>
}

export interface ProjectFileGroupPagesResponse {
  scopes: ProjectFileGroupMetadata[]
  pages: Array<ProjectFileFolderPage & ProjectFileGroupPageRequest>
  program_options?: Array<{ id: number; name: string }>
  valid_selection_keys?: string[]
  selected_files?: Array<{
    project_id: number | string
    file_id: string
    key: string
    filename: string
  }>
}

export const $getProjectFileGroupPages = async (
  request: ProjectFileGroupPagesRequest,
  signal?: AbortSignal,
) => $http.post<ProjectFileGroupPagesResponse>('file-management/file-groups/', {
  ...request,
  paginated: true,
}, { signal })

export const $getProjectFiles = async (projectId: number | string, folder?: string, additionalParams?: Record<string, any>) => {
  const params: Record<string, any> = { ...additionalParams }
  if (folder) {
    params.folder = folder
  }
  return await $http.get<ProjectFilesListResponse>(`file-management/projects/${projectId}/files/`, {
    params: Object.keys(params).length ? params : undefined,
  })
}

export const $getProjectFilesBatch = async (
  scopes: ProjectFilesBatchScope[],
  folder?: string,
) => {
  return await $http.post<ProjectFilesBatchListResponse>('file-management/files/batch/', {
    scopes,
    folder: folder || undefined,
  })
}

export const $getProjectFileGroups = async (
  scopes: ProjectFilesBatchScope[],
  folder?: string,
  search?: string,
  folderPages?: ProjectFileFolderPagesRequest,
  programIds?: number[],
  campaignType?: number | null,
  ordering?: string,
) => {
  return await $http.post<ProjectFileGroupsListResponse>('file-management/file-groups/', {
    scopes,
    folder: folder || undefined,
    search: search?.trim() || undefined,
    folder_pages: folderPages,
    program_ids: programIds?.length ? programIds : undefined,
    campaign_type: campaignType ?? undefined,
    ordering: ordering || undefined,
  })
}

/**
 * Fetch signed download URLs for one file or multiple files stored in S3.
 * If called with `key`, returns a single metadata object.
 * If called with `folder` + `code`, returns all matching files for that item.
 */
export const $getS3FileDownloadUrls = async (
  projectId: number | string,
  params: {
    key?: string
    file_id?: string
    folder?: string
    code?: string
    intent?: 'download' | 'preview'
    selected_item_ids?: string
    work_order_id?: number | string
  },
  expiresIn = 20,
): Promise<any> => {
  const res = await $http.get(`file-management/projects/${projectId}/file/`, {
    params: { ...params, expires_in: expiresIn },
  })
  return res.data
}

export const $deleteProjectFile = async (
  projectId: number | string,
  params: {
    key: string
    selected_item_ids?: string
    work_order_id?: number | string
  },
) => {
  return await $http.delete(`file-management/projects/${projectId}/file/`, { params })
}

export type S3Folder =
  | 'ARTWORK'
  | 'PREPRESS_PROOF'
  | 'INSTALLATION'
  | 'SCOPE'
  | 'SCOPING_PHOTOS'

export interface MultiFolderFileItem {
  key: string
  download_url: string
  preview_url?: string | null
  size?: number
  content_type?: string
  etag?: string
  last_modified?: string
  file?: ProjectFileRecord
  folder?: S3Folder
  filename?: string
  code?: string
}

export interface MultiFolderFileResponse {
  results: MultiFolderFileItem[]
}

export interface KeyFileItem {
  key: string
  download_url: string
  preview_url?: string | null
  size?: number
  content_type?: string
  etag?: string
  last_modified?: string
  file?: ProjectFileRecord
}

export interface ProjectFileControlLinkedItem {
  type: 'sign' | 'branding' | 'kop'
  id: string
  label: string
  url: string
}

export interface ProjectFileControlVersion {
  id: string
  key: string
  name: string
  extension: string
  filename: string
  preview_path?: string | null
  preview_url?: string | null
  created?: string
  modified?: string
  created_by?: number | null
  created_by_display?: string | null
  modified_by?: number | null
  modified_by_display?: string | null
  is_historical: boolean
  previous_name?: string | null
  history_action?: 'renamed' | 'archived' | null
}

export interface ProjectFileControlPayload {
  name: string
  extension: string
  created?: string
  modified?: string
  created_by?: number | null
  created_by_display?: string | null
  modified_by?: number | null
  modified_by_display?: string | null
  linked_items: ProjectFileControlLinkedItem[]
  main_version: ProjectFileControlVersion | null
  historical_files: ProjectFileControlVersion[]
  archived_files: ProjectFileControlVersion[]
}

export interface ProjectFileControlActionPayload {
  file_id: string
  selected_item_ids?: string
  work_order_id?: number | string
}

export interface ProjectFileControlWaitingRoomDuplicateContext {
  archive_replacement_file_id: string
}

export interface ProjectFileControlActionResponse {
  detail: string
  control: ProjectFileControlPayload
  waiting_room_duplicate_file?: WaitingRoomFile | null
  waiting_room_duplicate_context?: ProjectFileControlWaitingRoomDuplicateContext | null
}

export interface MultiFolderDownloadParams {
  code: string
  folders: readonly S3Folder[]
  intent?: 'download' | 'preview'
  selected_item_ids?: string
  work_order_id?: number | string
  key?: never
}

export interface KeyDownloadParams {
  key: string
  intent?: 'download' | 'preview'
  selected_item_ids?: string
  work_order_id?: number | string
  code?: never
  folders?: never
}

export type S3DownloadParams = MultiFolderDownloadParams | KeyDownloadParams
export type S3DownloadResponse = MultiFolderFileResponse | KeyFileItem

const isKeyDownloadParams = (
  params: S3DownloadParams,
): params is KeyDownloadParams => typeof params.key === 'string'

/**
 * Fetch download URLs either by an exact S3 key or by code across folders.
 */
export const $getS3FileDownloadUrlsMultiFolders = async (
  projectId: number | string,
  params: S3DownloadParams,
  expiresIn = 900,
): Promise<S3DownloadResponse> => {
  const query = new URLSearchParams()

  query.set('intent', params.intent ?? 'download')
  query.set('expires_in', String(expiresIn))
  if (params.selected_item_ids) {
    query.set('selected_item_ids', params.selected_item_ids)
  }
  if (params.work_order_id) {
    query.set('work_order_id', String(params.work_order_id))
  }

  if (isKeyDownloadParams(params)) {
    query.set('key', params.key)
  }
  else {
    if (!params.folders.length)
      throw new Error('At least one folder is required')

    query.set('code', params.code)
    // Produces: folders=ARTWORK&folders=INSTALLATION
    params.folders.forEach(folder => query.append('folders', folder))
  }

  const response = await $http.get(
    `file-management/projects/${projectId}/files/search/`,
    { params: query },
  )

  return response.data
}

export const $getProjectFileControl = async (
  projectId: number | string,
  params: {
    key: string
    selected_item_ids?: string
    work_order_id?: number | string
  },
) => {
  return await $http.get<ProjectFileControlPayload>(
    `file-management/projects/${projectId}/file-control/`,
    { params },
  )
}

export const $makeMainProjectFileVersion = async (
  projectId: number | string,
  payload: ProjectFileControlActionPayload,
) => {
  return await $http.post<ProjectFileControlActionResponse>(
    `file-management/projects/${projectId}/file-control/make-main/`,
    payload,
  )
}

export const $deleteProjectFileVersion = async (
  projectId: number | string,
  payload: ProjectFileControlActionPayload,
) => {
  return await $http.post<ProjectFileControlActionResponse>(
    `file-management/projects/${projectId}/file-control/delete/`,
    payload,
  )
}

export interface FileReassignOption {
  id: number
  identity: string
}

export const $getProjectFileReassignOptions = async (
  projectId: number | string,
  params: { file_id: string; search?: string; page?: number; per_page?: number },
) => await $http.get<{ results: FileReassignOption[]; count: number; next: boolean }>(
  `file-management/projects/${projectId}/file-control/reassign-options/`,
  { params },
)

export const $reassignProjectFile = async (
  projectId: number | string,
  payload: { file_id: string; target_item_id: number },
) => await $http.post<{
  detail: string
  outcome: 'reassigned' | 'waiting_room'
  control?: ProjectFileControlPayload
}>(`file-management/projects/${projectId}/file-control/reassign/`, payload)
